import { useState, useMemo, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Monitor, Brain, Sparkles, Target, BarChart3,
  Cloud, Settings, ChevronLeft, Search as SearchIcon,
  X, LogOut, Menu, Compass
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useLogoutConfirmation } from '@/hooks/useLogoutConfirmation';
import { useSidebarLayout } from './SidebarLayoutContext';
import { useGuideTour } from '@/context/GuideTourContext';
import { skillService } from '@/services/skills';
import { HUBS } from '@/routes/hubRoutes';
import { KhiimoriLogo } from '@/components/brand/KhiimoriLogo';
import { useUIMode } from '@/context/UIModeContext';

interface NavItem {
  path: string;
  icon: React.ElementType;
  label: string;
}

/**
 * Sidebar nav shows the 8 hubs as a flat list — no section grouping,
 * no expand/collapse, no count badges. The Cmd+K palette is the
 * discovery surface for everything else.
 *
 * The "live skill count" is pulled from `/api/v1/skills` so the header
 * always shows how many agent capabilities are available.
 */
const ICON_MAP: Record<string, React.ElementType> = {
  LayoutDashboard,
  Monitor,
  Brain,
  Sparkles,
  Target,
  BarChart3,
  Cloud,
  Settings,
};

const HUB_ITEMS: NavItem[] = HUBS.map((hub) => ({
  path: hub.path,
  icon: ICON_MAP[hub.iconName] ?? LayoutDashboard,
  label: hub.title,
}));

export function UnifiedSidebar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [skillCount, setSkillCount] = useState<number | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const { collapsed: userCollapsed, setCollapsed } = useSidebarLayout();
  const collapsed = userCollapsed;
  const { startTour, isTourActive } = useGuideTour();

  // Pull a live count of registered skills for the header chip.
  useEffect(() => {
    let mounted = true;
    skillService
      .listSkills({})
      .then((skills) => {
        if (mounted) setSkillCount(skills.length);
      })
      .catch(() => {
        if (mounted) setSkillCount(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const user = useAuthStore((state) => state.user);
  const { promptLogout, logoutConfirmDialog } = useLogoutConfirmation();
  const initial =
    (user?.full_name?.[0] || user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase();

  // Keyboard shortcut to toggle collapse
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault();
        setCollapsed(c => !c);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    promptLogout();
  };

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return HUB_ITEMS;
    const q = searchQuery.toLowerCase();
    return HUB_ITEMS.filter((item) => item.label.toLowerCase().includes(q));
  }, [searchQuery]);

  const headerCount = skillCount ?? HUB_ITEMS.length;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-lg transition-all duration-150 group text-sm
    ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
    ${isActive
      ? 'bg-primary-500/15 text-primary-300 border-l-[3px] border-primary-500 ml-0'
      : 'text-khiimori-text-secondary/55 hover:bg-khiimori-bg-tertiary/50 hover:text-khiimori-text-secondary/80 border-l-[3px] border-transparent'
    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40`;

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div 
        data-tour="sidebar-brand"
        className={`flex shrink-0 items-center border-b border-khiimori-border ${collapsed ? 'justify-center px-2 h-14' : 'px-4 h-14 gap-3'}`}
      >
        <KhiimoriLogo size={32} className="shadow-lg shadow-primary-500/25" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold tracking-tight text-khiimori-text-primary">Khiimori</div>
            <div className="text-[10px] text-khiimori-text-secondary/35">
              {headerCount} skills
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <div className={`shrink-0 px-3 py-2 ${collapsed ? 'hidden' : ''}`}>
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-khiimori-text-secondary/30" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search hubs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-khiimori-border bg-khiimori-bg-tertiary/50 py-1.5 pl-8 pr-7 text-xs text-khiimori-text-primary placeholder:text-khiimori-text-secondary/25 outline-none transition-colors focus:border-primary-500/50 focus:bg-khiimori-bg-tertiary"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-khiimori-text-secondary/30 hover:text-khiimori-text-primary"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* Flat nav list */}
      <nav className="no-scrollbar flex-1 overflow-y-auto px-2 py-2" aria-label="Main navigation">
        <div className="space-y-0.5">
          {filteredItems.map((item) => {
            const Icon = item.icon;
            // Generate data-tour tag for key hubs
            const tourTag = item.path === '/tabs' ? 'nav-tabs' : item.path === '/ai' ? 'nav-ai' : item.path === '/apps' ? 'nav-apps' : item.path === '/settings' ? 'nav-settings' : undefined;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-tour={tourTag}
                className={navLinkClass}
                aria-label={collapsed ? item.label : undefined}
              >
                <Icon className={`h-4 w-4 shrink-0 ${collapsed ? 'mx-auto' : ''}`} />
                {!collapsed && (
                  <span className="truncate font-medium">{item.label}</span>
                )}
              </NavLink>
            );
          })}
        </div>

        {/* No results */}
        {filteredItems.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-khiimori-text-secondary/30">
            No hubs matching "{searchQuery}"
          </div>
        )}
      </nav>

      {/* Guided Tour Trigger Button */}
      <div className="shrink-0 border-t border-khiimori-border/60 p-2">
        <button
          onClick={() => startTour(0)}
          data-tour="nav-tour"
          className={`flex w-full items-center rounded-lg transition-all duration-150 ${
            collapsed
              ? 'justify-center p-2 text-primary-400 hover:bg-primary-500/15'
              : 'gap-2 px-3 py-2 text-xs font-medium text-primary-300 hover:bg-primary-500/15 hover:text-primary-200'
          }`}
          title="Start Guided Tour"
          aria-label="Start Guided Tour"
        >
          <Compass className={`h-4 w-4 shrink-0 text-primary-400 ${isTourActive ? 'animate-spin' : ''}`} />
          {!collapsed && <span>Guided App Tour</span>}
        </button>
      </div>

      {/* Collapse toggle */}
      <div className="shrink-0 border-t border-khiimori-border p-2">
        <button
          onClick={() => setCollapsed(c => !c)}
          data-tour="nav-shortcuts"
          className={`flex w-full items-center rounded-lg text-khiimori-text-secondary/40 transition-colors hover:bg-khiimori-bg-tertiary/50 hover:text-khiimori-text-secondary/70 ${collapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2'}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <ChevronLeft className={`h-4 w-4 shrink-0 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          {!collapsed && <span className="text-xs">Collapse (Ctrl+B)</span>}
        </button>
      </div>

      {/* User section */}
      <div className="shrink-0 border-t border-khiimori-border p-2">
        <div className={`flex items-center rounded-lg ${collapsed ? 'justify-center' : 'gap-2.5 px-2 py-1.5'}`}>
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/30 to-khiimori-accent-fuchsia/30 text-xs font-bold text-khiimori-text-primary">
            {initial}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium text-khiimori-text-primary">
                {user?.full_name || user?.name || 'User'}
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className={`flex shrink-0 items-center justify-center rounded-md text-khiimori-text-secondary/40 transition-colors hover:bg-khiimori-bg-tertiary hover:text-khiimori-text-primary ${collapsed ? 'mt-1' : ''}`}
            style={{ width: collapsed ? 28 : 32, height: collapsed ? 28 : 32 }}
            aria-label="Log out"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {logoutConfirmDialog}
      {/* Desktop sidebar */}
      <aside
        className={`fixed left-0 top-0 z-40 hidden h-screen flex-col border-r border-khiimori-border lg:flex transition-[width] duration-300 ease-in-out ${
          collapsed ? 'w-16' : 'w-60'
        }`}
        style={{
          background: 'linear-gradient(180deg, var(--khiimori-bg-secondary), var(--khiimori-bg-tertiary))',
          backdropFilter: 'blur(20px)',
        }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed left-4 top-4 z-50 flex h-10 w-10 items-center justify-center rounded-xl bg-khiimori-bg-tertiary text-khiimori-text-secondary/70 backdrop-blur-xl lg:hidden"
        aria-label="Open menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60] modal-backdrop lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: -320 }}
              animate={{ x: 0 }}
              exit={{ x: -320 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 z-[70] h-full w-72 lg:hidden"
              style={{
                background: 'linear-gradient(180deg, var(--khiimori-bg-secondary), var(--khiimori-bg-tertiary))',
                backdropFilter: 'blur(20px)',
                borderRight: '1px solid var(--glass-border)',
              }}
            >
              {/* Mobile close button */}
              <div className="flex items-center justify-between border-b border-khiimori-border p-4">
                <div className="flex items-center gap-2">
                  <KhiimoriLogo size={28} />
                  <span className="text-sm font-bold text-khiimori-text-primary">Khiimori</span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg p-1.5 text-khiimori-text-secondary/50 hover:bg-khiimori-bg-tertiary hover:text-khiimori-text-primary"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {/* On mobile, show a flat list (no grouping) */}
              <UnifiedSidebarMobile
                items={HUB_ITEMS}
                user={user}
                onLogout={handleLogout}
                onClose={() => setMobileOpen(false)}
                onStartTour={() => startTour(0)}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function UnifiedSidebarMobile({
  items,
  user,
  onLogout,
  onClose,
  onStartTour,
}: {
  items: NavItem[];
  user: any;
  onLogout: () => void;
  onClose: () => void;
  onStartTour: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(i => i.label.toLowerCase().includes(q));
  }, [searchQuery, items]);

  return (
    <div className="flex h-[calc(100%-57px)] flex-col">
      {/* Search */}
      <div className="shrink-0 p-3">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-khiimori-text-secondary/30" />
          <input
            type="text"
            placeholder="Search hubs..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-khiimori-border bg-khiimori-bg-tertiary/50 py-2.5 pl-9 pr-4 text-sm text-khiimori-text-primary placeholder:text-khiimori-text-secondary/30 outline-none focus:border-primary-500/50"
          />
        </div>
      </div>

      <nav className="no-scrollbar flex-1 overflow-y-auto px-2">
        <div className="space-y-0.5 p-1">
          {filtered.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-500/15 text-primary-300'
                      : 'text-khiimori-text-secondary/55 hover:bg-khiimori-bg-tertiary/50 hover:text-khiimori-text-secondary/80'
                  }`
                }
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <div className="shrink-0 border-t border-khiimori-border p-3">
        <div className="mb-3 flex items-center gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary-500/30 to-khiimori-accent-fuchsia/30 text-sm font-bold text-khiimori-text-primary">
            {(user?.full_name?.[0] || user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-khiimori-text-primary">
              {user?.full_name || user?.name || 'User'}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            onClose();
            onStartTour();
          }}
          className="mb-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-primary-300 transition-colors hover:bg-primary-500/15 hover:text-primary-200"
        >
          <Compass className="h-4 w-4 text-primary-400" />
          Guided App Tour
        </button>
        <button
          onClick={onLogout}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-khiimori-text-secondary/50 transition-colors hover:bg-khiimori-bg-tertiary/50 hover:text-khiimori-text-primary"
        >
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>
    </div>
  );
}